import React, { forwardRef } from 'react';
import { Breadcrumb } from 'ui/breadcrumb';
import {
  BreadcrumbsContainer,
  DocLink,
  HeaderColLeft,
  HeaderColRight,
  HeaderContent,
  PageHeaderContainer,
  PageActionsContainer,
  PageSubSectionWrapper,
  PageHeaderContent,
} from './PageHeader.styled';
import { PAGE_HEADER_VARIANTS } from './constants';
import { LinkContent, PageHeaderProps } from './types';

const generateDocTitle = (
  children: React.ReactNode | string,
  docTitle?: string,
  breadcrumb?: LinkContent,
) => {
  if (docTitle) {
    return docTitle;
  }
  if (typeof children === 'string') {
    return breadcrumb ? `${children} | ${breadcrumb.content}` : children;
  }
  return 'Iterable';
};

const PageHeader = forwardRef(function PageHeader(
  {
    breadcrumb,
    variant = PAGE_HEADER_VARIANTS.PRIMARY,
    documentation,
    pageSubSection,
    pageActions,
    isAngularLink,
    documentTitle,
    children,
    isPastScrollBreakpoint = false,
    isSticky = false,
    navBarHeight = 0,
    folderBreadcrumb,
    hasNormalPageWidth = false,
    ...props
  }: PageHeaderProps,
  ref: React.Ref<HTMLDivElement>,
) {
  document.title = generateDocTitle(children, documentTitle, breadcrumb);

  return (
    <PageHeaderContainer
      data-has-scrolled={isPastScrollBreakpoint}
      data-is-sticky={isSticky}
      data-variant={variant}
      isPastScrollBreakpoint={isPastScrollBreakpoint}
      navBarHeight={navBarHeight}
      ref={ref}
      {...props}
    >
      <PageHeaderContent data-page-header-content data-use-page-max-width={hasNormalPageWidth}>
        <HeaderColLeft>
          {breadcrumb && (
            <BreadcrumbsContainer>
              <Breadcrumb isAngularLink={isAngularLink} breadcrumb={breadcrumb} variant={variant} />
              {folderBreadcrumb && <Breadcrumb variant={variant} breadcrumb={folderBreadcrumb} />}
            </BreadcrumbsContainer>
          )}
          <HeaderContent
            data-test="page-header-content"
            data-variant={variant}
            isPastScrollBreakpoint={isPastScrollBreakpoint}
          >
            {children}
          </HeaderContent>
          {pageSubSection && (
            <PageSubSectionWrapper
              data-test="page-header-page-sub-section"
              data-variant={variant}
              isPastScrollBreakpoint={isPastScrollBreakpoint}
            >
              {pageSubSection}
            </PageSubSectionWrapper>
          )}
        </HeaderColLeft>
        <HeaderColRight data-variant={variant}>
          {documentation && (
            <DocLink
              hasOpenNewTabIcon
              data-test="page-header-documentation-link"
              href={documentation.href}
              target="_blank"
            >
              {documentation.content}
            </DocLink>
          )}
          {pageActions && (
            <PageActionsContainer data-test="page-header-page-actions">
              {pageActions}
            </PageActionsContainer>
          )}
        </HeaderColRight>
      </PageHeaderContent>
    </PageHeaderContainer>
  );
});

export default PageHeader;
